// ============================================================
// BLOQUE 32 — RFW-READDY-NOTIFICATIONS-001
// Tipos para notificaciones internas y preferencias del oyente.
// ============================================================

/** Tipos de notificación confirmados como eventos reales del sistema. */
export type NotificationType =
  | 'PROGRAM_STARTING'
  | 'RADIO_LIVE'
  | 'ANNOUNCER_ON_AIR'
  | 'REQUEST_ACCEPTED'
  | 'REQUEST_REJECTED'
  | 'REQUEST_ATTENDED'
  | 'REQUEST_PLAYED'
  | 'NEW_PODCAST'
  | 'CONTEST_STARTED'
  | 'CONTEST_ENDING'
  | 'CONTEST_WINNER'
  | 'RADIO_ANNOUNCEMENT'
  | 'SYSTEM_NOTICE';

/** Etiquetas legibles para cada tipo de notificación. */
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  PROGRAM_STARTING: 'Programa por comenzar',
  RADIO_LIVE: 'Radio en vivo',
  ANNOUNCER_ON_AIR: 'Locutor al aire',
  REQUEST_ACCEPTED: 'Solicitud aceptada',
  REQUEST_REJECTED: 'Solicitud rechazada',
  REQUEST_ATTENDED: 'Solicitud atendida',
  REQUEST_PLAYED: 'Canción reproducida',
  NEW_PODCAST: 'Nuevo podcast',
  CONTEST_STARTED: 'Concurso iniciado',
  CONTEST_ENDING: 'Concurso por terminar',
  CONTEST_WINNER: 'Ganador de concurso',
  RADIO_ANNOUNCEMENT: 'Aviso de radio',
  SYSTEM_NOTICE: 'Aviso del sistema',
};

/** Íconos Remix Icon para cada tipo de notificación. */
export const NOTIFICATION_TYPE_ICONS: Record<NotificationType, string> = {
  PROGRAM_STARTING: 'ri-timer-line',
  RADIO_LIVE: 'ri-broadcast-line',
  ANNOUNCER_ON_AIR: 'ri-mic-line',
  REQUEST_ACCEPTED: 'ri-check-double-line',
  REQUEST_REJECTED: 'ri-close-circle-line',
  REQUEST_ATTENDED: 'ri-user-voice-line',
  REQUEST_PLAYED: 'ri-music-line',
  NEW_PODCAST: 'ri-headphone-line',
  CONTEST_STARTED: 'ri-trophy-line',
  CONTEST_ENDING: 'ri-hourglass-line',
  CONTEST_WINNER: 'ri-medal-line',
  RADIO_ANNOUNCEMENT: 'ri-megaphone-line',
  SYSTEM_NOTICE: 'ri-information-line',
};

/**
 * Notificación individual tal como existe en la tabla `notifications`.
 * El backend PHP es responsable de insertar registros; el frontend solo lee.
 */
export interface Notification {
  id: string;
  profile_id: string;
  type: NotificationType;
  title: string;
  message: string;
  is_read: boolean;
  priority: 'normal' | 'high' | 'urgent';
  related_entity_type: string | null;
  related_entity_id: string | null;
  action_url: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/** Filtros para consultar notificaciones. */
export interface NotificationFilters {
  type?: NotificationType;
  is_read?: boolean;
  priority?: 'normal' | 'high' | 'urgent';
  related_entity_type?: string;
  related_entity_id?: string;
  page?: number;
  per_page?: number;
  sort?: 'newest' | 'oldest' | 'priority';
  search?: string;
}

/** Respuesta paginada de notificaciones. */
export interface NotificationPage {
  notifications: Notification[];
  total: number;
  page: number;
  per_page: number;
  unread_count: number;
}

/** Conteo simple de no leídas. */
export interface UnreadCount {
  total: number;
  urgent: number;
  high: number;
}

// ============================================================
// PREFERENCIAS
// ============================================================

/** Tipos de preferencia soportados. */
export type PreferenceType =
  | 'global'
  | 'radio'
  | 'program'
  | 'announcer'
  | 'podcast'
  | 'contest'
  | 'request'
  | 'admin_notice';

/** Etiquetas para cada tipo de preferencia. */
export const PREFERENCE_TYPE_LABELS: Record<PreferenceType, string> = {
  global: 'Notificaciones globales',
  radio: 'Radio seguida',
  program: 'Programas',
  announcer: 'Locutores',
  podcast: 'Podcasts',
  contest: 'Concursos',
  request: 'Solicitudes',
  admin_notice: 'Avisos administrativos',
};

/** Preferencia individual del oyente. */
export interface NotificationPreference {
  id: string;
  profile_id: string;
  preference_type: PreferenceType;
  reference_id: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/** Payload para actualizar una preferencia. */
export interface UpdatePreferencePayload {
  preference_type: PreferenceType;
  reference_id?: string | null;
  enabled: boolean;
}

/** Payload para actualizar varias preferencias a la vez. */
export interface BulkUpdatePreferencesPayload {
  preferences: UpdatePreferencePayload[];
}

/** Mapa de preferencias agrupadas por tipo. */
export type PreferencesMap = Record<PreferenceType, NotificationPreference[]>;

// ============================================================
// REALTIME
// ============================================================

/** Callback que se invoca cuando llega una notificación nueva vía Realtime. */
export type OnNewNotification = (notification: Notification) => void;

/** Estado de la suscripción Realtime. */
export type RealtimeSubscriptionStatus = 'subscribed' | 'unsubscribed' | 'error';